/* eslint-disable @nx/enforce-module-boundaries */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
  type MRT_TableOptions,
} from 'material-react-table';
import {
  Pagination,
  Typography,
  TextField,
  Button,
  Box,
  InputAdornment,
  useMediaQuery,
  PaginationItem,
} from '@mui/material';
import searchIcon from 'packages/novous_web/src/assets/icons/Icon.svg';
import filterIcon from 'packages/novous_web/src/assets/icons/Filter.svg';
import Custom from 'packages/novous_web/src/assets/icons/CustomBlue.svg';
import download from 'packages/novous_web/src/assets/icons/download.svg';
import add from 'packages/novous_web/src/assets/icons/plusWhite.svg';
import bulkUpload from 'packages/novous_web/src/assets/icons/bulkUpload.svg';
import BulkUploadModal from 'packages/novous_web/src/common/components/BulkUpload';
import CustomIconButton from 'packages/novous_web/src/common/components/CustomIconButton';
import CustomiseTableModal from 'packages/novous_web/src/common/components/CustomiseTable';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from 'packages/novous_web/src/api/endpoints';
import useAxios from 'packages/novous_web/src/api/useAxios';
import { useAuth } from 'packages/novous_web/src/authContext/useAuth';
import useDebounce from 'packages/novous_web/src/hooks/useDebounce';
import DownloadData from 'packages/novous_web/src/common/components/DownloadData';
import FilterDataModel from 'packages/novous_web/src/common/components/FilterDataModal';
import CustomFilterModal from 'packages/novous_web/src/common/components/CustomFilterModal';
import useApiError from 'packages/novous_web/src/api/useApiError';
import AlertToast from 'packages/novous_web/src/common/components/Alert';

interface Route {
  routeCode: string;
  routeName: string;
  tag: string;
  vendor: string;
  sourceName: string;
  destinationName: string;
}

type ColumnObjType = {
  [key: string]: {
    name: string;
    status: boolean;
  };
};

interface GroupOption {
  value: string;
  label: string;
}

interface FilterField {
  key: string;
  label: string;
  options?: { value: string; label: string }[];
  defaultValue?: string;
  input?: boolean;
}
interface TripDataTableProps {
  handleTile: (data: ColumnObjType) => void;
}
const RouteDataTable: React.FC<TripDataTableProps> = ({ handleTile }) => {
  const [toast, setToast] = useState({ open: false, message: '', type: 'error' });
  const [searchQuery, setSearchQuery] = useState('');
  const [data, setData] = useState<Route[]>([]);
  const [filteredData, setFilteredData] = useState<Route[]>([]);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState<string>('pdf');
  const [isFilterModelOpen, setIsFilterModelOpen] = useState(false);
  // New state for the customize modal
  const [isCustomiseModalOpen, setIsCustomiseModalOpen] = useState(false);
  // Column configuration state
  const [columnObj, setColumnObj] = useState<ColumnObjType>({
    routeCode: { name: 'Route Code', status: true },
    routeName: { name: 'Route Name', status: true },
    tag: { name: 'Tag', status: true },
    vendor: { name: 'Vendor', status: true },
    sourceName: { name: 'Source', status: true },
    destinationName: { name: 'Destination', status: true },
    actions: { name: 'Actions', status: true },
  });

  const isSmallScreen = useMediaQuery('(max-width:850px)');
  const [currentPage, setCurrentPage] = useState(1);
  const paginatedArr = useRef<any[]>([null]);
  const { user } = useAuth();
  const navigate = useNavigate();
  const debouncedSearchQuery = useDebounce(searchQuery, 200);
  const [filterData, setFilterData] = useState<{ data: any; isfiltered: boolean }>({
    data: {},
    isfiltered: false,
  });
  const [isUploadModelOpen, setIsUploadModelOpen] = useState(false);
  const handleUploadModalClose = () => setIsUploadModelOpen(false);

  const pageSize = 5;
  const {
    data: groupsData,
    error: groupsError,
    loading: groupsLoading,
    fetchData: fetchGroupsData,
  } = useAxios<{ data: any }>(`${API_ENDPOINTS.dashboard.GROUP}${user?.customer_id}`, 'GET');

  let filterFileds: FilterField[] = [];

  filterFileds.push({
    key: 'source',
    label: 'Source',
    input: true,
  });

  filterFileds.push({
    key: 'destination',
    label: 'Destination',
    input: true,
  });

  const {
    data: filterRoutesData,
    error: filterRoutesError,
    loading: filterRoutesLoading,
    fetchData: fetchFilterRoutesData,
  } = useAxios<any>(`${API_ENDPOINTS.directories.ROUTES_FILTER}`, 'POST');

  useEffect(() => {
  if (filterData.isfiltered) {
    console.log('Triggering ROUTES_FILTER API with:', filterData);
    fetchFilterRoutesData({
      sourceName: filterData.data.source,
      destinationName: filterData.data.destination,
      lastEvaluatedKey: null,
      fleetId: user?.customer_id,
    });
    setCurrentPage(1);
  } else {
    fetchRoutesData({ fleetId: user?.customer_id, lastEvaluatedKey: null });
  }
}, [filterData]);

  const {
    data: downloadRoutesData,
    error: downloadRoutesError,
    loading: downloadRoutesLoading,
    fetchData: fetchDownloadRoutesData,
  } = useApiError<any>(`${API_ENDPOINTS.directories.ROUTES_DOWNLOAD}`, 'POST', 'blob');

  // const handleDownload = async () => {
  //   fetchDownloadRoutesData({ fleetId: user?.customer_id, fileType: selectedFormat });
  // };

  const handleDownload = async () => {
    const requestData = {
      fleetId: user?.customer_id,
      fileType: selectedFormat,
    };

    try {
      const response = await fetchDownloadRoutesData(requestData);
      if (
        response?.type === 'text/csv' ||
        response?.type === 'application/pdf' ||
        response?.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ) {
        setToast({
          open: true,
          message: `File has been downloaded successfully`,
          type: 'success',
        });
      } else {
        setToast({
          open: true,
          message: response?.error,
          type: 'error',
        });
      }
    } catch (error) {
      console.error('Error downloading data:', error);
      setToast({
        open: true,
        message: (error as Error).message,
        type: 'error',
      });
    }
  };

  const handleFormatChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedFormat(event.target.value);
  };

  const {
    data: routesData,
    error: routesError,
    loading: routesLoading,
    fetchData: fetchRoutesData,
  } = useAxios<{ data: { arrayObjectList: any[]; lastEvaluatedKey: null } }>(
    `${API_ENDPOINTS.dashboard.GET_ROUTES}`,
    'POST',
  );

  // useEffect(() => {
  //   fetchRoutesData({ fleetId: user?.customer_id, lastEvaluatedKey: null });
  // }, [user?.customer_id]);

  useEffect(() => {
    console.info('Routes API Response:', routesData);
    if (routesData?.data) {
      const routeListData: Route[] = routesData?.data?.arrayObjectList.map((route) => ({
        routeCode: route.routeCode,
        routeName: route.routeName,
        tag: route.tag,
        vendor: route.vendor,
        sourceName: route.sourceName,
        destinationName: route.destinationName,
      }));
      setData(routeListData);
    }
  }, [routesData]);

  useEffect(() => {
  if (filterRoutesData) {
    console.log('ROUTES_FILTER API response data:', filterRoutesData);
  }
  if (filterRoutesData?.data?.lastEvaluatedKey) {
    if (paginatedArr.current.length === currentPage) {
      paginatedArr.current.push(filterRoutesData.data.lastEvaluatedKey);
    }
  }
  if (filterRoutesData?.data) {
    // Use arrayObjectList for filtered routes
    const routeListData: Route[] = filterRoutesData?.data?.arrayObjectList.map((route: any) => ({
      routeCode: route.routeCode,
      routeName: route.routeName,
      tag: route.tag,
      vendor: route.vendor,
      sourceName: route.sourceName,
      destinationName: route.destinationName,
    }));
    setData(routeListData);
  } else if (filterRoutesData?.data == null) {
    setData([]);
  }
}, [filterRoutesData]);

  useEffect(() => {
    if (routesData?.data?.lastEvaluatedKey) {
      if (paginatedArr.current.length === currentPage) {
        paginatedArr.current.push(routesData.data.lastEvaluatedKey);
      }
    }
  }, [routesData, currentPage]);

  useEffect(() => {
    if (downloadRoutesData) {
      try {
        if (downloadRoutesData instanceof Blob) {
          const url = window.URL.createObjectURL(downloadRoutesData);
          const a = document.createElement('a');
          a.href = url;
          let extension = selectedFormat.toLowerCase();
          if (extension === 'excel') {
            extension = 'xlsx'; // Use xlsx for Excel format
          }

          const fileName = `route_data.${extension}`;
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        } else {
          console.error('Invalid response format. Expected a Blob.');
        }
      } catch (error) {
        console.error('Error downloading file:', error);
      } finally {
        setIsDownloadModalOpen(false);
      }
    }
  }, [downloadRoutesData]);

  const handlePageChange = async (event: React.ChangeEvent<unknown>, page: number) => {
    if (page !== 1) {
      await fetchRoutesData({
        fleetId: user?.customer_id,
        lastEvaluatedKey: paginatedArr.current[page - 1],
      });
    } else {
      await fetchRoutesData({ fleetId: user?.customer_id, lastEvaluatedKey: null });
    }
    setCurrentPage(page);
  };

  const handleReset = () => {
    console.info('Reset clicked!');
    setTilesObj({
      Stat1: { name: 'Total Routes', status: true },
      Stat2: { name: 'Scheduled Routes', status: true },
      Stat3: { name: 'Deviated Routes', status: true },
      Stat4: { name: 'Unused Routes', status: true },
    });
    // your reset logic here
  };

  useEffect(() => {
    totalPages.current = paginatedArr.current.length;
  }, [paginatedArr.current.length]);

  const totalPages = useRef(paginatedArr.current.length);
  useEffect(() => {
    if (debouncedSearchQuery) {
      const filteredDataArr = data.filter((route) =>
        route.routeName.toLowerCase().includes(searchQuery.toLowerCase()),
      );
      setCurrentPage(1);
      totalPages.current = 1;
      setFilteredData(filteredDataArr);
    } else {
      setFilteredData(data);
      totalPages.current = paginatedArr.current.length;
    }
  }, [debouncedSearchQuery, data]);
  // Define the base columns before filtering with columnObj
  const baseColumns: MRT_ColumnDef<Route>[] = useMemo(
    () => [
      {
        accessorKey: 'routeCode',
        header: 'Route Code',
        enableColumnActions: false,
        size: isSmallScreen ? 90 : 180,
        muiTableBodyCellProps: {
          sx: { maxWidth: '150px', padding: '8px', overflow: 'hidden' },
        },
        Cell: ({ cell }) => (
          <Typography variant="body2" fontWeight={400} noWrap>
            {cell.getValue<string>()}
          </Typography>
        ),
      },
      {
        accessorKey: 'routeName',
        header: 'Route Name',
        muiTableBodyCellProps: {
          sx: { padding: '8px' },
        },
        enableColumnActions: false,
        size: isSmallScreen ? 90 : 180,
        Cell: ({ cell }) => (
          <Typography variant="body2" fontWeight={400} noWrap>
            {cell.getValue<string>()}
          </Typography>
        ),
      },
      {
        accessorKey: 'tag',
        header: 'Tag',
        muiTableBodyCellProps: {
          sx: { padding: '8px' },
        },
        enableColumnActions: false,
        size: isSmallScreen ? 90 : 180,
        Cell: ({ cell }) => (
          <Typography variant="body2" fontWeight={400} noWrap>
            {cell.getValue<string>()}
          </Typography>
        ),
      },
      {
        accessorKey: 'vendor',
        header: 'Vendor',
        muiTableBodyCellProps: {
          sx: { padding: '8px' },
        },
        enableColumnActions: false,
        size: isSmallScreen ? 90 : 180,
        Cell: ({ cell }) => (
          <Typography variant="body2" fontWeight={400} noWrap>
            {cell.getValue<string>()}
          </Typography>
        ),
      },
      {
        accessorKey: 'sourceName',
        header: 'Source',
        muiTableBodyCellProps: {
          sx: { padding: '8px', whiteSpace: 'normal', wordBreak: 'break-word' },
        },
        enableColumnActions: false,
        size: isSmallScreen ? 90 : 180,
        Cell: ({ cell }) => (
          <Typography
            variant="body2"
            fontWeight={400}
            sx={{
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
            }}
          >
            {cell.getValue<string>()}
          </Typography>
        ),
      },
      {
        accessorKey: 'destinationName',
        header: 'Destination',
        muiTableBodyCellProps: {
          sx: { padding: '8px', whiteSpace: 'normal', wordBreak: 'break-word' },
        },
        enableColumnActions: false,
        size: isSmallScreen ? 90 : 180,
        Cell: ({ cell }) => (
          <Typography
            variant="body2"
            fontWeight={400}
            sx={{
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
            }}
          >
            {cell.getValue<string>()}
          </Typography>
        ),
      },
      {
        accessorKey: 'actions',
        header: 'Actions',
        muiTableBodyCellProps: {
          sx: { padding: '8px' },
        },
        enableColumnActions: false,
        size: isSmallScreen ? 90 : 180,
        Cell: ({ row }) => (
          <Typography
            variant="body2"
            fontWeight={700}
            color="primary"
            sx={{ cursor: 'pointer' }}
            onClick={() => navigate(`${row.original.routeCode}`, { state: row.original })}
          >
            View
          </Typography>
        ),
      },
    ],
    [isSmallScreen, navigate],
  );
  const [tilesObj, setTilesObj] = useState<ColumnObjType>({
    Stat1: { name: 'Total Routes', status: true },
    Stat2: { name: 'Scheduled Routes', status: true },
    Stat3: { name: 'Deviated Routes', status: true },
    Stat4: { name: 'Unused Routes', status: true },
  });
  useEffect(() => {
    handleTile(tilesObj);
  }, [tilesObj]);

  // Filter the columns based on columnObj settings
  const columns: MRT_ColumnDef<Route>[] = useMemo(
    () => baseColumns.filter((column) => columnObj[column.accessorKey as keyof ColumnObjType]?.status),
    [baseColumns, columnObj],
  );

  const tableOptions: MRT_TableOptions<Route> = {
    columns,
    data: filteredData,
    enablePagination: false,
    enableColumnFilters: false,
    enableSorting: false,
    enableGlobalFilter: false,
    enableDensityToggle: false,
    enableHiding: false,
    enableBottomToolbar: false,
    enableTopToolbar: false,
    enableFullScreenToggle: false,
    enableColumnResizing: false,
    enableToolbarInternalActions: false,
    initialState: {
      columnPinning: {
        left: ['routeCode', 'routeName'],
        right: ['actions'],
      },
    },
    muiTableBodyProps: {
      sx: {
        padding: 0,
        margin: 0,
      },
    },
  };

  const table = useMaterialReactTable(tableOptions);
  const handleDownloadModalClose = () => {
    setIsDownloadModalOpen(false);
  };

  return (
    <>
      <AlertToast
        open={toast.open}
        message={toast.message}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        type={toast.type as any}
      />
      <DownloadData
        downloadLoading={downloadRoutesLoading}
        isDownloadModalOpen={isDownloadModalOpen}
        handleDownloadModalClose={handleDownloadModalClose}
        selectedFormat={selectedFormat}
        handleFormatChange={handleFormatChange}
        handleDownload={handleDownload}
        modalTitle="Download Trips Data"
        modalDescription="Please select a file format for your download."
        formatLabel="Format"
        formatExcelLabel="Excel"
        formatCsvLabel="CSV"
        formatPdfLabel="PDF"
        cancelButtonText="Cancel"
        downloadButtonText="Download"
      />
      <div className="flex flex-col gap-4 w-full">
        {/* Search Input */}
        <div className="w-full flex justify-between gap-4 xs:flex-col">
          <div className="flex gap-2 items-center">
            <div className="max-w-[400px]">
              <TextField
                variant="outlined"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search"
                sx={(theme) => ({
                  '& .MuiOutlinedInput-root': {
                    borderColor: theme.palette.action.selected,
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    height: '100%',
                  },
                  '& .MuiInputBase-root': {
                    padding: '0rem 1rem',
                  },
                  '& .MuiOutlinedInput-input': {
                    padding: '0px 12px',
                    height: '48px',
                    display: 'flex',
                    alignItems: 'center',
                  },
                })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <img src={searchIcon} alt="icon" width={16} height={16} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment
                      position="end"
                      sx={(theme) => ({
                        height: '48px',
                        maxHeight: '48px',
                        display: 'flex',
                        alignItems: 'center',
                        borderLeft: `1px solid ${theme.palette.action.selected}`,
                        paddingLeft: '8px',
                      })}
                    >
                      <img
                        src={filterIcon}
                        alt="icon"
                        width={24}
                        height={24}
                        style={{ cursor: 'pointer' }}
                        onClick={() => setIsFilterModelOpen(true)}
                      />
                    </InputAdornment>
                  ),
                }}
                inputProps={{
                  style: {
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: '14px',
                    fontWeight: 500,
                  },
                }}
              />
            </div>
            {filterData.isfiltered && (
              <CustomIconButton
                text={`Clear Filters (${
                  Object.values(filterData.data).filter((value) => value !== '').length
                })`}
                background="transparent"
                outline={false} // Set to true if you want an outline
                textColor="#C83333"
                onClick={() => setFilterData({ data: {}, isfiltered: false })}
              />
            )}
          </div>
          <div className="flex gap-4 flex-wrap">
            <CustomIconButton
              icon={<img src={Custom} alt="Custom" width={16} height={16} />}
              text="Customize"
              background="transparent"
              outline={false}
              onClick={() => setIsCustomiseModalOpen(true)}
            />
            <CustomIconButton
              icon={<img src={bulkUpload} alt="Bulk Upload" width={24} height={24} />}
              text="Bulk Upload"
              background="transparent"
              outline={false}
              onClick={() => setIsUploadModelOpen(true)}
            />
            <CustomIconButton
              icon={
                <img
                  src={download}
                  alt="Download"
                  width={16}
                  height={16}
                  style={filteredData.length === 0 ? { filter: 'grayscale(1)', opacity: 0.5 } : undefined}
                />
              }
              text="Download"
              background="transparent"
              variant="outlined"
              isDisabled={filteredData.length === 0}
              onClick={() => setIsDownloadModalOpen(true)}
            />
            <CustomIconButton
              icon={<img src={add} alt="Add" width={16} height={16} />}
              text="Add Route"
              variant="contained"
              onClick={() => navigate('new')}
            />
          </div>
        </div>
        <MaterialReactTable table={table} />
        <Box display="flex" justifyContent="center" mt={2}>
          <Pagination
            count={totalPages.current}
            page={currentPage}
            onChange={handlePageChange}
            shape="rounded"
            color="primary"
            siblingCount={0}
            boundaryCount={0}
            renderItem={(item) => {
              if (item.type === 'page' && item.page !== null && item.page > currentPage) {
                return null;
              }
              return <PaginationItem {...item} />;
            }}
          />
        </Box>
      </div>
      <CustomFilterModal
        open={isFilterModelOpen}
        onClose={() => setIsFilterModelOpen(false)}
        onReset={() => console.info('Reset filter!')}
        title="Filter Routes"
        filterFields={filterFileds}
        onApply={(data: any) => setFilterData({ data: data, isfiltered: true })}
        description="Use the options below to refine your search and find specific routes quickly."
      />
      {/* Render the CustomiseTableModal when the customize icon is clicked */}
      <CustomiseTableModal
        open={isCustomiseModalOpen}
        onClose={() => setIsCustomiseModalOpen(false)}
        onReset={handleReset}
        title="Customize Routes"
        description="Choose which sections to display and arrange them the way you like."
        columnObj={columnObj}
        tilesObj={tilesObj}
        setTilesObj={setTilesObj}
        setColumnObj={setColumnObj}
      />
      {isUploadModelOpen && (
        <BulkUploadModal
          open={isUploadModelOpen}
          handleClose={handleUploadModalClose}
          uploadEndpoint={API_ENDPOINTS.directories.ROUTE_BULK_UPLOAD}
          title="Bulk Upload"
          description="Upload an Excel/CSV files to add multiple routes at once!"
          entityType="routes"
          sampleFileUrl={API_ENDPOINTS.directories.ROUTE_SAMPLE_DOWNLOAD}
        />
      )}
    </>
  );
};

export default RouteDataTable;
